"use client";

import isEmpty from "lodash/isEmpty";

import { Typography } from "@/components/Typography";
import {
  PreviewFacilitiesCard,
  PreviewLocationBlock,
  PreviewPrimaryAction,
  PreviewSocialNetworks,
} from "@/features/previews/shared";
import { PhoneLayout } from "@/features/previews/layouts/PhoneLayout";
import type { QRProps } from "@/contexts/QREditorContext/types";
import { BusinessContactInfo, BusinessHeader, BusinessOpeningHours } from "./components";
import { useBusinessData } from "./hooks";

import "./BusinessTemplate.scss";

export const BusinessTemplate = (props: QRProps<"business">) => {
  const { translations, isInitialPreview, data, addressData, hasOpeningHours, hasContactInfo } =
    useBusinessData(props);

  if (!data) {
    return null;
  }

  const {
    subtitle,
    buttonText,
    buttonUrl,
    themeColor,
    businessHours,
    facilities,
    aboutTheCompany,
    socialNetworks,
  } = data;

  return (
    <PhoneLayout
      page="business"
      dataQA="preview-business-container"
      backgroundColor={themeColor?.backgroundColor}
      isInitPreview={isInitialPreview}
      hasIncludeBrowser
    >
      <div className="business-template">
        <BusinessHeader
          data={data}
          isInitialPreview={isInitialPreview ?? false}
        />

        <div className="business-template__content qr-content">
          {subtitle && (
            <div className="business-template__subtitle">
              <Typography
                Tag="p"
                classname="u-spacing-3"
              >
                {subtitle}
              </Typography>
            </div>
          )}

          {buttonText && (
            <PreviewPrimaryAction
              text={buttonText}
              url={buttonUrl}
              buttonColor={themeColor?.buttonColor}
              className="business-template__primary-action"
              spacing={2}
            />
          )}

          {hasOpeningHours && businessHours && (
            <BusinessOpeningHours
              businessHours={businessHours}
              translations={translations}
            />
          )}

          <PreviewLocationBlock
            data={addressData}
            title={translations?.location_title ?? ""}
          />

          <PreviewFacilitiesCard
            facilities={facilities}
            title={translations?.preview_facilities}
            variant="business"
          />

          {hasContactInfo && (
            <BusinessContactInfo
              data={data}
              translations={translations}
            />
          )}

          {aboutTheCompany && (
            <div className="u-card">
              <Typography
                Tag="p"
                size="m"
                classname="u-semibold"
              >
                {translations?.preview_about_the_company}
              </Typography>
              <Typography
                Tag="p"
                classname="u-spacing-0 u-grey business-template__about"
              >
                {aboutTheCompany}
              </Typography>
            </div>
          )}

          {!isEmpty(socialNetworks) && (
            <PreviewSocialNetworks
              networks={socialNetworks}
              variant="business"
              className="business-template__social-networks"
            />
          )}
        </div>
      </div>
    </PhoneLayout>
  );
};
